import { gql } from '@/__generated__/gql';
import { DealPipelineInvestmentWorkflowStateFiltersQuery, DueDiligenceRecordStatus, DueDiligenceRecordTierStatus } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import {
  DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY,
  UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION
} from '@/components/dealPipeline-page/queries';
import { Loader } from '@/components/loaders/loader';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DUE_DILIGENCE_RECORD_QUERY, UPDATE_RECORD_STATUS_MUTATION, UPDATE_TIER_STATUS_MUTATION } from '../queries';
import StrategyStatus from './strategyStatus';

const RECORD_STATUS_DIALOG_INVESTMENT_QUERY = gql(`
  query RecordStatusDialogInvestment($id: String!) {
    Investment(id: $id) {
      id
      securityStatus
      workflowInstance {
        id
        currentState {
          label
          name
        }
      }
    }
  }
`);

const getRecordStatusModalConfig = (recordStatus: DueDiligenceRecordStatus) => {
  if (recordStatus === DueDiligenceRecordStatus.Approved) {
    return {
      title: 'Approve Due Diligence',
      confirmButtonName: 'Approve',
      description: 'Please provide reasons for determining why this opportunity is approved at this time.'
    };
  }

  return {
    title: 'Reject Due Diligence',
    confirmButtonName: 'Reject',
    description: 'Please provide reasons for determining why this opportunity is unsuitable at this time.'
  };
};

const workflowStatesMap: Record<string, string> = {
  APPROVED: 'recommended',
  REJECTED: 'rejected'
};

interface RecordStatusProps {
  isOpen: boolean;
  currentRow: any;
  recordId: string | null;
  strategyId?: string;
  recordStatus: DueDiligenceRecordStatus;
  tierStatus: DueDiligenceRecordTierStatus;
  skipReasonInput?: boolean;
  onClose: () => void;
}

const RecordStatus = ({
  isOpen,
  currentRow,
  recordId,
  strategyId,
  recordStatus,
  tierStatus,
  skipReasonInput = false,
  onClose
}: RecordStatusProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [reasonValue, setReasonValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflowStates, setWorkflowStates] = useState<DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters']>([]);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateInvestmentSecurityStatusTransition] = useMutation(UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION);
  const [updateTierStatus] = useMutation(UPDATE_TIER_STATUS_MUTATION);
  const [updateRecordStatus] = useMutation(UPDATE_RECORD_STATUS_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [DUE_DILIGENCE_RECORD_QUERY]
  });

  const { data: investmentQueryData, loading: investmentQueryLoading } = useQuery(RECORD_STATUS_DIALOG_INVESTMENT_QUERY, {
    skip: !strategyId,
    variables: { id: strategyId },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: workflowStatesQueryLoading } = useQuery(DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, {
    onCompleted: ({ InvestmentWorkflowStateFilters }) => {
      setWorkflowStates(InvestmentWorkflowStateFilters);
    }
  });

  const handleConfirm = () => {
    if (investmentQueryData?.Investment?.workflowInstance?.currentState?.name !== 'dueDiligence') {
      setIsConfirmationDialogOpen(true);
      return;
    }
    handleUpdateRecordStatus({});
  };

  const handleUpdateRecordStatus = async ({ skipUpdateWorkflowState = false }: { skipUpdateWorkflowState?: boolean }) => {
    if (!recordId) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);

      // Update strategy workflow state in the background (no user prompt)
      if (!skipUpdateWorkflowState) {
        const workflowState = workflowStates.find((state) => state.name === workflowStatesMap[recordStatus]);
        if (workflowState) {
          await updateInvestmentSecurityStatusTransition({
            variables: {
              data: {
                investmentId: investmentQueryData?.Investment?.id,
                rawInput: { destinationStateId: workflowState.id }
              }
            }
          });
        }
      }

      await updateTierStatus({
        variables: {
          data: {
            id: currentRow.data.id,
            ...(!skipReasonInput ? { note: reasonValue } : {}),
            status: tierStatus
          }
        }
      });

      await updateRecordStatus({
        variables: {
          data: {
            id: recordId,
            ...(!skipReasonInput ? { note: reasonValue } : {}),
            status: recordStatus
          }
        }
      });

      setReasonValue('');
      onClose();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error(error);
      setIsConfirmationDialogOpen(false);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const { title, confirmButtonName, description } = getRecordStatusModalConfig(recordStatus);

  if (isConfirmationDialogOpen || skipReasonInput) {
    return (
      <StrategyStatus
        isOpen={isConfirmationDialogOpen || skipReasonInput}
        recordStatus={recordStatus}
        workflowStates={workflowStates}
        investmentData={investmentQueryData?.Investment}
        handleUpdateRecordStatus={handleUpdateRecordStatus}
        onClose={onClose}
      />
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      confirmButton={{
        name: confirmButtonName,
        onClick: handleConfirm,
        disabled: !reasonValue.length || loading || investmentQueryLoading || workflowStatesQueryLoading
      }}
      showCancelButton={!loading}
      showCloseIcon={!loading}
      isUseOutsideClick={!loading}
      showRequiredFields
    >
      {investmentQueryLoading || workflowStatesQueryLoading ? (
        <Loader />
      ) : (
        <>
          {loading ? (
            <ModalLoader />
          ) : (
            <RecordStatusWrapper>
              {errorMessage && (
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              )}
              <ModalText>{description}</ModalText>
              <Textarea label="Reason" value={reasonValue} onChange={(e) => setReasonValue(e.target.value)} size="md" autoFocus isRequiredField />
            </RecordStatusWrapper>
          )}
        </>
      )}
    </ModalWrappedContent>
  );
};

export default RecordStatus;

const RecordStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ModalText = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.base')};
`;
